import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { OiqLayoutHeaderComponent } from './header/header.component';
import { OiqLayoutAccountModel } from './layout.models';
import { OiqLayoutService } from './layout.service';
import { LogoType } from './logo/logo.component';
import { OiqLayoutNavItemModel } from './nav/nav-item.models';
import { OiqLayoutNavMetaUrls, OiqLayoutNavPath, OiqLayoutNavRouteMeta } from './nav/nav.models';
import {
  oiqLayoutNavDetermineUrlNodes,
  oiqLayoutNavMergeRouteMeta,
  oiqLayoutNavRouteMetaUrls,
} from './nav/nav.utils';
import { OiqLayoutSidebarComponent } from './sidebar/sidebar.component';

@Component({
  selector: 'oiq-v2-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [CommonModule, RouterModule, OiqLayoutHeaderComponent, OiqLayoutSidebarComponent],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'oiq-layout',
  },
})
export class OiqLayoutComponent implements AfterViewInit {
  @HostBinding('class.isEmpty') get classEmpty(): boolean {
    return this.isEmpty;
  }
  @HostBinding('class.isLoading') get classLoading(): boolean {
    return this.isLoading;
  }
  @HostBinding('class.isExpanded') get classExpanded(): boolean {
    return this.isSidebarExpanded;
  }
  @HostBinding('class.isLarge') get classLarge(): boolean {
    return !this.isSmall && !this.isMedium;
  }
  @HostBinding('class.isMedium') get classMedium(): boolean {
    return this.isMedium;
  }
  @HostBinding('class.isSmall') get classSmall(): boolean {
    return this.isSmall;
  }

  @Output() logout = new EventEmitter<void>();
  @Output() searched = new EventEmitter<string>();
  @Input() brandImg: string | null = '';
  @Input() logoType: LogoType = 'jpeg';
  @Input() menu: OiqLayoutNavItemModel[] = [];
  @Input() settingsMenu: OiqLayoutNavItemModel | null = null;
  @Input() isImpersonated: boolean = false;
  @Input() isEmpty: boolean = false;
  @Input() isLoading: boolean = false;

  @Input() account: OiqLayoutAccountModel | null = null;

  @Input() emailAppEnabled: boolean = false;

  public layout = inject(OiqLayoutService);
  private _cdr = inject(ChangeDetectorRef);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);

  public navPath: OiqLayoutNavPath | null = null;
  public navRouteMeta: OiqLayoutNavRouteMeta | null = null;
  public navRouteMetaUrls: OiqLayoutNavMetaUrls | null = null;

  public isSidebarExpanded: boolean = false;
  public isMedium: boolean = false;
  public isSmall: boolean = false;
  public navigationEnd$ = this._router.events.pipe(filter(event => event instanceof NavigationEnd));

  constructor() {
    // Initialize navigation path to prevent ExpressionChangedAfterItHasBeenCheckedError
    this._initializeNavPath();

    this.layout.isSmall$.subscribe(_isSmall => {
      this.isSmall = _isSmall;
      this._cdr.markForCheck();
    });
    this.layout.isMedium$.subscribe(_isMedium => {
      this.isMedium = _isMedium;
      this._cdr.markForCheck();
    });
    this.layout.sidebarExpanded$.subscribe(_isExpanded => {
      this.isSidebarExpanded = _isExpanded;
      this._cdr.markForCheck();
    });
    this.navigationEnd$.subscribe((event: NavigationEnd) => {
      this._handleRouteChanged();
    });
  }

  ngAfterViewInit(): void {
    this._handleRouteChanged();
  }

  private _initializeNavPath(): void {
    // Initialize with default values to prevent ExpressionChangedAfterItHasBeenCheckedError
    this.navRouteMeta = {
      path: ['/'],
      child: [],
      params: {},
      data: {},
    };
    this.navRouteMetaUrls = {
      parent: '',
      child: '',
      current: '/',
      url: '/',
    };
    this.navPath = {
      url: '/',
      view: null,
      nodes: null,
    };
  }

  private _handleRouteChanged(): void {
    this.navRouteMeta = oiqLayoutNavMergeRouteMeta(this._route);
    this.navRouteMetaUrls = oiqLayoutNavRouteMetaUrls(this.navRouteMeta, {});

    this.navPath = {
      url: this.navRouteMetaUrls.url,
      view: null,
      nodes: oiqLayoutNavDetermineUrlNodes(this.navRouteMetaUrls.url, this.menu),
    };

    if (!this.menu.length) {
      // this._buildMenu();
      return;
    }
    this._cdr.markForCheck();
  }
}
